import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { gql } from '@apollo/client/core';
import { CultureInfoResponse } from '@ats/api/cm';
import { CMOption } from '@ats/cm/ui';
import { atsSort } from '@ats/utility';
import { Apollo } from 'apollo-angular';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

const SUPPORTED_CULTURES = gql`
	query supportedCultures {
		clientSponsor {
			name
			supportedCultures {
				label
				ietfBcp47Name
			}
		}
	}
`;

@Component({
    selector: 'cm-supported-culture-menu',
    templateUrl: './cm-supported-culture-menu.component.html',
    styleUrls: ['./cm-supported-culture-menu.component.scss'],
    standalone: false
})
export class CmSupportedCultureMenuComponent implements OnInit {
	@Input() filter: string[] | CMOption[];
	@Output() select = new EventEmitter<string>();
	cultures$: Observable<CMOption[]>;
	private filteredCulturesSubj$: Subject<string[]> = new Subject<string[]>();

	constructor(private readonly apollo: Apollo) {}

	ngOnInit(): void {
		const filteredCultures$ = this.filteredCulturesSubj$.asObservable().pipe(startWith([]), shareReplay(1));
		this.cultures$ = combineLatest([this.fetchSupportedCultures$(), filteredCultures$]).pipe(
			map(([cultures, filtered]) =>
				(filtered || []).length ? cultures.filter(culture => !filtered.includes(culture.value)) : cultures
			)
		);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.filter) {
			const filteredOptions =
				this.filter && typeof this.filter[0] === 'string'
					? [...this.filter]
					: (this.filter || []).map(filteredCulture => filteredCulture.value);
			this.filteredCulturesSubj$.next([...filteredOptions]);
		}
	}

	selectCulture(culture: string) {
		this.select.emit(culture);
	}

	private fetchSupportedCultures$(): Observable<CMOption[]> {
		return this.apollo
			.watchQuery<{ clientSponsor: { name: string; supportedCultures: Partial<CultureInfoResponse>[] } }>({
				query: SUPPORTED_CULTURES,
			})
			.valueChanges.pipe(
				map(({ data }) =>
					((data.clientSponsor || {}).supportedCultures || []).map(culture => ({
						value: culture.ietfBcp47Name,
						display: culture.label,
					}))
				),
				atsSort('display')
			);
	}
}
